/**
 * Product image "loupe" magnifier.
 *
 * On desktop (hover-capable, fine pointer) a circular lens follows the cursor over a product image and
 * shows that part of the image magnified. Clicking still opens the theme's full-screen zoom dialog because
 * the lens ignores pointer events.
 */

const ZOOM_FACTOR = 2.5;
const LENS_SIZE = 220;
const HI_RES_WIDTH = 2000;
// Slideshow slides carry --zoomable; grid items don't, but they contain the zoom button.
const HOST_SELECTOR =
  '.product-media-container--image:is(.product-media-container--zoomable, :has(.product-media-container__zoom-button))';
const DESKTOP_QUERY = '(min-width: 750px) and (hover: hover) and (pointer: fine)';

const STYLES = `
  .tsn-loupe {
    position: fixed;
    top: 0;
    left: 0;
    width: ${LENS_SIZE}px;
    height: ${LENS_SIZE}px;
    border-radius: 50%;
    border: 3px solid #fff;
    box-shadow: 0 6px 24px rgb(0 0 0 / 0.35);
    background-repeat: no-repeat;
    background-color: #fff;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.15s ease;
    z-index: 100;
  }
  .tsn-loupe[data-active='true'] { opacity: 1; }
  @media (prefers-reduced-motion: reduce) { .tsn-loupe { transition: none; } }
`;

/** @type {HTMLElement | null} */
let lens = null;
/** @type {Set<string>} */
const preloaded = new Set();
const desktop = window.matchMedia(DESKTOP_QUERY);
let frame = 0;

function injectStyles() {
  if (document.getElementById('tsn-loupe-styles')) return;
  const style = document.createElement('style');
  style.id = 'tsn-loupe-styles';
  style.textContent = STYLES;
  document.head.appendChild(style);
}

/**
 * @param {HTMLImageElement} img
 * @returns {string}
 */
function getHighResUrl(img) {
  const source = img.dataset.maxResolution || img.currentSrc || img.src;
  try {
    const url = new URL(source, window.location.href);
    url.searchParams.set('width', String(HI_RES_WIDTH));
    return url.toString();
  } catch {
    return source;
  }
}

/** @param {string} url */
function preload(url) {
  if (preloaded.has(url)) return;
  preloaded.add(url);
  new Image().src = url;
}

/** @returns {HTMLElement} */
function getLens() {
  if (!lens) {
    lens = document.createElement('div');
    lens.className = 'tsn-loupe';
    lens.setAttribute('aria-hidden', 'true');
    document.body.appendChild(lens);
  }
  return lens;
}

function hideLens() {
  if (lens) lens.dataset.active = 'false';
}

/**
 * @param {Element} host
 * @param {MouseEvent} event
 */
function updateLens(host, event) {
  const img = host.querySelector('img.product-media__image');
  if (!(img instanceof HTMLImageElement) || !img.complete || !img.naturalWidth) return;

  const imgRect = img.getBoundingClientRect();
  const x = event.clientX - imgRect.left;
  const y = event.clientY - imgRect.top;

  if (x < 0 || y < 0 || x > imgRect.width || y > imgRect.height) {
    hideLens();
    return;
  }

  // Work out where the picture actually sits inside the <img> box (object-fit: cover / contain).
  const fit = getComputedStyle(img).objectFit;
  const scale =
    fit === 'contain'
      ? Math.min(imgRect.width / img.naturalWidth, imgRect.height / img.naturalHeight)
      : Math.max(imgRect.width / img.naturalWidth, imgRect.height / img.naturalHeight);
  const drawnWidth = img.naturalWidth * scale;
  const drawnHeight = img.naturalHeight * scale;
  const offsetX = (imgRect.width - drawnWidth) / 2;
  const offsetY = (imgRect.height - drawnHeight) / 2;

  const loupe = getLens();
  const url = getHighResUrl(img);
  preload(url);

  if (loupe.dataset.src !== url) {
    loupe.dataset.src = url;
    loupe.style.backgroundImage = `url("${url}")`;
  }

  const half = LENS_SIZE / 2;
  loupe.style.backgroundSize = `${drawnWidth * ZOOM_FACTOR}px ${drawnHeight * ZOOM_FACTOR}px`;
  loupe.style.backgroundPosition = `${half - (x - offsetX) * ZOOM_FACTOR}px ${half - (y - offsetY) * ZOOM_FACTOR}px`;
  loupe.style.transform = `translate(${event.clientX - half}px, ${event.clientY - half}px)`;
  loupe.dataset.active = 'true';
}

/** @param {MouseEvent} event */
function onPointerMove(event) {
  if (!desktop.matches || !(event.target instanceof Element)) return;
  const host = event.target.closest(HOST_SELECTOR);
  if (!host || host.closest('zoom-dialog, .dialog-zoomed-gallery')) return;

  cancelAnimationFrame(frame);
  frame = requestAnimationFrame(() => updateLens(host, event));
}

/** @param {MouseEvent} event */
function onPointerOut(event) {
  if (!(event.target instanceof Element)) return;
  const host = event.target.closest(HOST_SELECTOR);
  if (!host) return;
  const next = event.relatedTarget;
  if (next instanceof Node && host.contains(next)) return;
  cancelAnimationFrame(frame);
  hideLens();
}

injectStyles();
document.addEventListener('mousemove', onPointerMove, { passive: true });
document.addEventListener('mouseout', onPointerOut, { passive: true });